import { Cartao, TituloDeSecao } from '../../componentes/base';

export interface ContratoNoPortal {
  readonly clinica: string;
  readonly assinadoEm: string | null;
  readonly terminaEm: string | null;
  readonly diasRestantes: number | null;
  readonly situacao: 'sem_contrato' | 'vigente' | 'vencendo' | 'vencido';
}

/**
 * Início e fim do contrato, como a clínica vê.
 *
 * Um acesso pode abrir DUAS carteiras — é o caso do Dr. Álvaro. Quando os dois
 * contratos coincidem, sai uma linha só; quando divergem, sai uma por clínica,
 * porque aí a data de uma não vale para a outra e juntá-las mentiria.
 *
 * Contrato sem data cadastrada não vira aviso nem espaço vazio: o bloco inteiro
 * some. A clínica não tem o que fazer com "não informado", e a ausência é assunto
 * do escritório.
 */
export function Contratos({
  contratos,
}: {
  contratos: readonly ContratoNoPortal[];
}) {
  const comData = contratos.filter((c) => c.assinadoEm);
  if (comData.length === 0) return null;

  /* Duas carteiras com o mesmo contrato viram uma linha: repetir a mesma data com
     dois nomes diferentes sugere dois contratos onde há um. */
  const iguais =
    comData.length > 1 &&
    comData.every(
      (c) => c.assinadoEm === comData[0]!.assinadoEm && c.terminaEm === comData[0]!.terminaEm,
    );
  const linhas = iguais ? [comData[0]!] : comData;

  return (
    <Cartao>
      <TituloDeSecao auxiliar="para prorrogar, fale com o escritório">
        {linhas.length > 1 ? 'Contratos' : 'Contrato'}
      </TituloDeSecao>

      <div className="space-y-3">
        {linhas.map((c) => (
          <div
            key={c.clinica}
            className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm"
          >
            {!iguais && linhas.length > 1 && (
              <span className="w-full text-xs text-[color:var(--texto-tenue)]">
                {c.clinica}
              </span>
            )}
            <span className="text-[color:var(--texto-suave)]">
              Início{' '}
              <strong className="tabular font-semibold text-[color:var(--texto)]">
                {dataPorExtenso(c.assinadoEm)}
              </strong>
            </span>
            <span className="text-[color:var(--texto-suave)]">
              Fim{' '}
              <strong
                className="tabular font-semibold"
                style={{
                  color:
                    c.situacao === 'vencido'
                      ? 'var(--color-vencido)'
                      : c.situacao === 'vencendo'
                        ? 'var(--color-atencao)'
                        : 'var(--texto)',
                }}
              >
                {c.terminaEm ? dataPorExtenso(c.terminaEm) : 'sem prazo determinado'}
              </strong>
            </span>
          </div>
        ))}
      </div>
    </Cartao>
  );
}

/** `26/08/2026`. Vazio vira travessão, nunca "Invalid Date". */
function dataPorExtenso(iso: string | null): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}
